import type { FaceName } from "./blocks";

/** Four corners, CCW when viewed from outside so the winding normal points out. */
export function faceCorners(x: number, y: number, z: number, face: FaceName): number[][] {
  const x1 = x + 1;
  const y1 = y + 1;
  const z1 = z + 1;
  switch (face) {
    case "px":
      return [
        [x1, y, z1],
        [x1, y, z],
        [x1, y1, z],
        [x1, y1, z1],
      ];
    case "nx":
      return [
        [x, y, z],
        [x, y, z1],
        [x, y1, z1],
        [x, y1, z],
      ];
    case "py":
      return [
        [x, y1, z1],
        [x1, y1, z1],
        [x1, y1, z],
        [x, y1, z],
      ];
    case "ny":
      return [
        [x, y, z],
        [x1, y, z],
        [x1, y, z1],
        [x, y, z1],
      ];
    case "pz":
      return [
        [x, y, z1],
        [x1, y, z1],
        [x1, y1, z1],
        [x, y1, z1],
      ];
    case "nz":
      return [
        [x1, y, z],
        [x, y, z],
        [x, y1, z],
        [x1, y1, z],
      ];
  }
}

export function windingNormal(corners: number[][]): [number, number, number] {
  const a = corners[0]!;
  const b = corners[1]!;
  const c = corners[2]!;
  const ux = b[0]! - a[0]!;
  const uy = b[1]! - a[1]!;
  const uz = b[2]! - a[2]!;
  const vx = c[0]! - b[0]!;
  const vy = c[1]! - b[1]!;
  const vz = c[2]! - b[2]!;
  return [uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx];
}

/** AO side-pairs per vertex, matching `faceCorners` order. */
export const AO_CORNERS: Record<FaceName, [number, number, number][][]> = {
  py: [
    [
      [0, 1, 1],
      [1, 1, 0],
    ],
    [
      [1, 1, 0],
      [0, 1, -1],
    ],
    [
      [0, 1, -1],
      [-1, 1, 0],
    ],
    [
      [-1, 1, 0],
      [0, 1, 1],
    ],
  ],
  ny: [
    [
      [0, -1, -1],
      [1, -1, 0],
    ],
    [
      [1, -1, 0],
      [0, -1, 1],
    ],
    [
      [0, -1, 1],
      [-1, -1, 0],
    ],
    [
      [-1, -1, 0],
      [0, -1, -1],
    ],
  ],
  px: [
    [
      [1, -1, 0],
      [1, 0, 1],
    ],
    [
      [1, -1, 0],
      [1, 0, -1],
    ],
    [
      [1, 1, 0],
      [1, 0, -1],
    ],
    [
      [1, 1, 0],
      [1, 0, 1],
    ],
  ],
  nx: [
    [
      [-1, -1, 0],
      [-1, 0, -1],
    ],
    [
      [-1, -1, 0],
      [-1, 0, 1],
    ],
    [
      [-1, 1, 0],
      [-1, 0, 1],
    ],
    [
      [-1, 1, 0],
      [-1, 0, -1],
    ],
  ],
  pz: [
    [
      [0, -1, 1],
      [-1, 0, 1],
    ],
    [
      [0, -1, 1],
      [1, 0, 1],
    ],
    [
      [0, 1, 1],
      [1, 0, 1],
    ],
    [
      [0, 1, 1],
      [-1, 0, 1],
    ],
  ],
  nz: [
    [
      [0, -1, -1],
      [1, 0, -1],
    ],
    [
      [0, -1, -1],
      [-1, 0, -1],
    ],
    [
      [0, 1, -1],
      [-1, 0, -1],
    ],
    [
      [0, 1, -1],
      [1, 0, -1],
    ],
  ],
};
